import { useAction, useAtom } from '@reatom/npm-react'
import { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import { match } from 'ts-pattern'

import {
  cookingExploreFiltersAtom,
  graduatedExploreFiltersAtom,
  newExploreFiltersAtom,
  topGainersExploreFiltersAtom,
  trendingExploreFiltersAtom,
} from '@root/features/explore/atoms'
import {
  cookingSortByAtom,
  exploreCookingModel,
  exploreGraduatedModel,
  exploreTopGainersModel,
  topGainersSortByAtom,
} from '@root/features/explore/atoms/reatom-explore-top-gainers'
import { exploreTrendingModel } from '@root/features/explore/atoms/reatom-explore-trending'
import { EXPLORE_LIST_PERIOD_MAP } from '@root/features/explore/model/constants'
import { ExploreFeedType } from '@root/features/explore/model/explore-feed-type'

import { exploreNewPairsModel } from '../../../atoms/reatom-explore-new'
import { ExploreFilterButton } from '../../explore-filter'
import { ExploreSorting } from '../../explore-sorting'
import { ExploreTypeSelect } from '../../explore-type-select/explore-type-select'
import { ExploreList } from '../explore-list'

import { ExploreBlock } from './explore-block'

import type { Pair } from '@root/entities/pair'
import type { ExploreLayout } from '@root/features/explore/model'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  column: ExploreLayout.Column
  feed: ExploreFeedType
  onItemClick?: (options: { pair: Pair.PairMinimal }) => void
} & ClassNameProps

export const ExploreFeed: React.FC<Props> = memo(({ column, feed }) => {
  const listRef = useRef<HTMLDivElement>(null)
  const scrollToTopTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined)
  const model = useMemo(
    () =>
      match(feed)
        .with(ExploreFeedType.New, () => exploreNewPairsModel)
        .with(ExploreFeedType.TopGainers, () => exploreTopGainersModel)
        .with(ExploreFeedType.Cooking, () => exploreCookingModel)
        .with(ExploreFeedType.Graduated, () => exploreGraduatedModel)
        .with(ExploreFeedType.Trending, () => exploreTrendingModel)
        .exhaustive(),
    [feed],
  )

  const filterModel = useMemo(
    () =>
      match(feed)
        .with(ExploreFeedType.New, () => newExploreFiltersAtom)
        .with(ExploreFeedType.TopGainers, () => topGainersExploreFiltersAtom)
        .with(ExploreFeedType.Cooking, () => cookingExploreFiltersAtom)
        .with(ExploreFeedType.Graduated, () => graduatedExploreFiltersAtom)
        .with(ExploreFeedType.Trending, () => trendingExploreFiltersAtom)
        .exhaustive(),
    [feed],
  )

  const [paused, setPaused] = useAtom(model.paused)
  const [buffedCount] = useAtom(model.buffedCount)

  const [pairsState] = useAtom(model.resolvedPairsStateAtom)
  const load = useAction(model.load)

  const scrollListToTop = useCallback(() => {
    listRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }, [])

  const clearScrollToTopTimeout = useCallback(() => {
    if (scrollToTopTimeoutRef.current) {
      clearTimeout(scrollToTopTimeoutRef.current)
      scrollToTopTimeoutRef.current = undefined
    }
  }, [])

  useEffect(() => clearScrollToTopTimeout, [clearScrollToTopTimeout])

  const handlePausedChange = useCallback(
    (nextPaused: boolean) => {
      if (nextPaused) {
        clearScrollToTopTimeout()
      }
      setPaused(nextPaused)
    },
    [clearScrollToTopTimeout, setPaused],
  )

  useEffect(() => {
    if (!paused) {
      clearScrollToTopTimeout()

      scrollToTopTimeoutRef.current = undefined
      scrollListToTop()
    }
  }, [paused, clearScrollToTopTimeout, scrollListToTop])

  const priceChartInterval = useMemo(() => {
    return match(feed)
      .with(ExploreFeedType.New, () => 'seconds' as const)
      .otherwise(() => 'minutes' as const)
  }, [feed])

  const statsPeriod = EXPLORE_LIST_PERIOD_MAP[feed]

  const sortModel = useMemo(() => {
    return match(feed)
      .with(ExploreFeedType.TopGainers, () => topGainersSortByAtom)
      .with(ExploreFeedType.Cooking, () => cookingSortByAtom)
      .otherwise(() => undefined)
  }, [feed])

  return (
    <ExploreBlock
      paused={paused}
      buffedCount={buffedCount}
      onPausedChange={handlePausedChange}
      pairsState={pairsState}
      heading={<ExploreTypeSelect column={column} />}
      filter={<ExploreFilterButton model={filterModel} size="small" />}
      sort={sortModel ? <ExploreSorting model={sortModel} /> : undefined}
    >
      <ExploreList
        listRef={listRef}
        statsPeriod={statsPeriod}
        priceChartInterval={priceChartInterval}
        onRetry={load}
        pairsState={pairsState}
      />
    </ExploreBlock>
  )
})
